import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api, { mediaUrl, apiErr } from "@/lib/api";
import Modal from "@/components/admin/Modal";
import ImageUploader from "@/components/admin/ImageUploader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SIZES = ["XS", "S", "M", "L", "XL"];
const blank = () => ({
  title: "", slug: "", category_slug: "", price: 0, compare_at_price: null, description: "",
  images: [], variants: SIZES.map((s) => ({ size: s, sku: "", stock: 0 })),
  fabric: "", care: "", batch_limit: "", eco_savings: "", model_specs: "", size_guide: "",
  complete_the_look: [], featured: false, active: true, order: 0,
});

export default function AdminProducts() {
  const { user } = useAuth();
  const isManager = user.role === "store_manager";
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(blank());

  const load = () => api.get("/products", { params: { admin: true } }).then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCategories(data)); }, []);

  const openNew = () => { setEditing(null); setF(blank()); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setF({ ...blank(), ...p, variants: SIZES.map((s) => p.variants?.find((v) => v.size === s) || { size: s, sku: "", stock: 0 }) }); setOpen(true); };

  const setVar = (i, k, val) => setF((prev) => { const v = [...prev.variants]; v[i] = { ...v[i], [k]: k === "stock" ? parseInt(val || 0) : val }; return { ...prev, variants: v }; });

  const save = async () => {
    const payload = { ...f, price: parseFloat(f.price), compare_at_price: f.compare_at_price ? parseFloat(f.compare_at_price) : null,
      slug: f.slug || f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved."); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (p) => { if (!window.confirm(`Delete ${p.title}?`)) return; try { await api.delete(`/products/${p.id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center mb-8">
        <div><h1 className="font-heading text-4xl tracking-tight">Products & Inventory</h1><p className="font-body text-sm text-ink/60 mt-1">{isManager ? "Manage stock levels and product imagery." : "Full catalog control with size matrix & eco-metrics."}</p></div>
        {!isManager && <button onClick={openNew} className="adm-btn" data-testid="create-product-btn"><Plus size={14} /> Add Product</button>}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {products.map((p) => {
          const stock = (p.variants || []).reduce((s, v) => s + (v.stock || 0), 0);
          return (
            <div key={p.id} className="adm-card flex gap-4" data-testid={`product-row-${p.slug}`}>
              <div className="w-16 h-20 bg-cream overflow-hidden shrink-0"><img src={mediaUrl(p.images?.[0])} alt="" className="w-full h-full object-cover" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-heading text-lg leading-tight truncate">{p.title}</p>
                <p className="font-body text-xs text-ink/50">${p.price?.toFixed(2)} · {stock} in stock · {p.sold_count || 0} sold</p>
                <div className="flex gap-1 mt-2 flex-wrap">{(p.variants || []).map((v) => <span key={v.size} className={`text-[0.55rem] px-1.5 py-0.5 border ${v.stock > 0 ? "border-ink/20" : "border-destructive/40 text-destructive"}`}>{v.size}:{v.stock}</span>)}</div>
                <div className="mt-3 flex gap-3">
                  <button onClick={() => openEdit(p)} className="text-[0.6rem] uppercase tracking-wide2 flex items-center gap-1 hover:opacity-70" data-testid={`edit-product-${p.slug}`}><Pencil size={12} /> Edit</button>
                  {!isManager && <button onClick={() => del(p)} className="text-[0.6rem] uppercase tracking-wide2 text-destructive flex items-center gap-1"><Trash2 size={12} /> Delete</button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Product" : "New Product"} wide testId="product-modal">
        <div className="space-y-4">
          {!isManager && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="adm-label">Title</label><input className="adm-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="product-title-input" /></div>
                <div><label className="adm-label">Slug (auto if empty)</label><input className="adm-input" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} /></div>
                <div><label className="adm-label">Category</label>
                  <select className="adm-input" value={f.category_slug} onChange={(e) => setF({ ...f, category_slug: e.target.value })}>
                    <option value="">—</option>{categories.map((c) => <option key={c.id} value={c.slug}>{c.title}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="adm-label">Price</label><input className="adm-input" type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="product-price-input" /></div>
                  <div><label className="adm-label">Compare</label><input className="adm-input" type="number" value={f.compare_at_price || ""} onChange={(e) => setF({ ...f, compare_at_price: e.target.value })} /></div>
                </div>
              </div>
              <div><label className="adm-label">Description</label><textarea className="adm-input" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="adm-label">Fabric Composition</label><input className="adm-input" value={f.fabric} onChange={(e) => setF({ ...f, fabric: e.target.value })} /></div>
                <div><label className="adm-label">Care Instructions</label><input className="adm-input" value={f.care} onChange={(e) => setF({ ...f, care: e.target.value })} /></div>
                <div><label className="adm-label">Batch Limit</label><input className="adm-input" placeholder="1 of 12 made" value={f.batch_limit} onChange={(e) => setF({ ...f, batch_limit: e.target.value })} /></div>
                <div><label className="adm-label">Eco Savings</label><input className="adm-input" value={f.eco_savings} onChange={(e) => setF({ ...f, eco_savings: e.target.value })} /></div>
                <div><label className="adm-label">Size Guide</label><input className="adm-input" value={f.size_guide} onChange={(e) => setF({ ...f, size_guide: e.target.value })} /></div>
                <div><label className="adm-label">Model Specs</label><input className="adm-input" value={f.model_specs} onChange={(e) => setF({ ...f, model_specs: e.target.value })} /></div>
              </div>
            </>
          )}
          <ImageUploader value={f.images?.[0] || ""} onChange={(url) => setF({ ...f, images: url ? [url] : [] })} label="Product Image" recommended="900×1200 · 3:4" testId="product-image-uploader" />

          <div>
            <label className="adm-label">Size Matrix — SKU & Stock</label>
            <div className="grid grid-cols-5 gap-2">
              {f.variants.map((v, i) => (
                <div key={v.size} className="border border-ink/10 p-2">
                  <p className="font-heading text-lg text-center">{v.size}</p>
                  <input className="adm-input mt-1 text-[0.65rem] px-1" placeholder="SKU" value={v.sku} onChange={(e) => setVar(i, "sku", e.target.value)} />
                  <input className="adm-input mt-1 text-center" type="number" value={v.stock} onChange={(e) => setVar(i, "stock", e.target.value)} data-testid={`variant-stock-${v.size}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 font-body text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} className="accent-ink" disabled={isManager} /> Featured</label>
            <label className="flex items-center gap-2 font-body text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="accent-ink" /> Active</label>
          </div>
          <button onClick={save} className="adm-btn w-full" data-testid="product-save-btn">Save Product</button>
        </div>
      </Modal>
    </div>
  );
}
